import {logger} from './logger'

type Action<T> = (state: T) => T

export type Timeout = ReturnType<typeof setTimeout>

export type ArgType<F> = F extends (...args: infer A) => any ? A : never

export type Updater<T> = (action: Action<T>) => () => void

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyFunction = (...args: any[]) => any

export type Dict<T> = {[key: string]: T}

export type Defined<T> = T extends infer D | undefined ? D : T

export const noop =
  <T = void>() =>
  (_: T) => {
    // noop
  }

export const notDefined = (v: unknown): v is undefined =>
  typeof v === 'undefined' || v === null

const toDebugStr = (v: unknown): string => {
  if (v && typeof v === 'object') {
    try {
      return JSON.stringify(v)
    } catch (e) {
      return String(v)
    }
  }
  return String(v)
}

/**
 * Curried version of isUndefinedOr
 */
export const optional = <T>(
  isType: (obj: unknown) => obj is T
): ((obj: unknown) => obj is T | undefined) => {
  return (obj: unknown): obj is T | undefined =>
    obj === undefined || isType(obj)
}

export const isUndefinedOr = <T>(
  isType: (obj: unknown) => obj is T,
  obj: unknown
): obj is T | undefined => {
  return obj === undefined || isType(obj)
}

export const isUndefined = (val?: unknown): boolean => {
  return val === undefined
}

export class EnsureError extends Error {}

/**
 * This is like `assert` but checks in runtime prod env, so
 * it is better to use different name, `assert` can be misleading
 */
export function ensure<T>(
  condition: T extends Promise<unknown> ? 'Promises are not allowed here!' : T,
  errorMessage: string,
  ...params: unknown[]
): asserts condition {
  if (!condition) {
    const msg =
      errorMessage +
      (params?.length > 0 ? ' ' + params.map(toDebugStr).join(', ') : '')
    throw new EnsureError(msg)
  }
}

export const logError = <T>(fn: () => Promise<T>): (() => Promise<T>) => {
  return async (): Promise<T> => {
    try {
      return await fn()
    } catch (e) {
      logger.log('Error', e)
      throw e
    }
  }
}

type WithKey<K extends string> = {[key in K]: unknown}
export const hasKey = <K extends string>(
  obj: unknown,
  k: K
): obj is WithKey<K> => {
  return obj instanceof Object && k in obj
}
